import type { CityConfig } from "../city-template/types";

export const brewerConfig: CityConfig = {
  city: "Brewer",
  state: "Maine",
  stateAbbr: "ME",
  slug: "brewer",
  route: "/cities/brewer",
  title: "Emergency & Fast Passport Renewal Brewer ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Brewer, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Brewer, Bangor, Orono, Holden, and Penobscot County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Brewer, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Brewer and the greater Bangor area travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Brewer%20Maine%20small%20city%20across%20the%20river%20from%20Bangor%2C%20historic%20riverfront%20with%20Penobscot%20River%20bridge%20view%2C%20charming%20downtown%20with%20classic%20New%20England%20buildings%20and%20tree-lined%20streets%2C%20beautiful%20afternoon%20light%20reflecting%20on%20water%2C%20professional%20photography%2C%20scenic%20riverfront%20with%20Bangor%20skyline%20in%20background%2C%20no%20visible%20text%20or%20people%2C%20clean%20Maine%20river%20town%20atmosphere&width=1800&height=700&seq=brewer-me-hero-001&orientation=landscape",
  heroImageAlt: "Brewer Maine historic riverfront along the Penobscot River with view of Bangor",
  geoPosition: "44.7967;-68.7614",
  geoPlacename: "Brewer, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 135 miles (2.5 hours) from Brewer, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Brewer", zips: "04412" },
    { name: "Bangor", zips: "04401" },
    { name: "Orono", zips: "04469" },
    { name: "Holden", zips: "04429" },
    { name: "Old Town", zips: "04468" },
    { name: "Veazie", zips: "04401" },
    { name: "Hampden", zips: "04444" },
    { name: "Milford", zips: "04461" },
    { name: "Eddington", zips: "04428" },
    { name: "Clifton", zips: "04428" },
    { name: "Carmel", zips: "04419" },
    { name: "Levant", zips: "04456" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Brewer departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Bangor", route: "/cities/bangor" },
    { name: "Emergency passport help in Old Town", route: "/cities/old-town" },
    { name: "Urgent passport services in Orono", route: "/cities/orono" },
    { name: "Same-day passport help in Portland", route: "/cities/portland-me" },
    { name: "Expedited passport services in Augusta", route: "/cities/augusta-me" },
    { name: "Passport courier services in Lewiston", route: "/cities/lewiston" },
    { name: "Rush passport help in Hampden", route: "/cities/hampden" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Brewer, Maine remotely via secure courier. We do not have a walk-in office in Brewer — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Brewer travelers.",
  fpcOfficeAddress: "One Boston Place, #2600, Boston, MA 02108",
  fpcOfficeDistance: "Serving greater Bangor and Penobscot County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Brewer — directly across the Penobscot River from Bangor. From downtown Brewer along the riverfront to the suburban neighborhoods of Holden and Levant — we help Brewer and the greater Bangor area travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Brewer neighborhoods and nearby cities including Bangor, Orono, Holden, Old Town, Veazie, Hampden, Milford, and Eddington. If you are in the greater Bangor area, we have you covered.",
  howItWorksIntro: "Most Brewer travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "Brewer travelers heading out on cruises, Canadian trips, and European vacations often need their passports in a hurry. Here are the most common mistakes we see.",
  testimonialsIntro: "Brewer travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Brewer? Here are the most common ones we hear from the greater Bangor area.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  dmvWebsite: "https://www.maine.gov/sos/bmv",
  dmvLabel: "Maine BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Brewer international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Laurie M.",
      location: "Brewer, ME",
      rating: 5,
      text: "Booked a Caribbean cruise with friends and realized my passport expired. Fast Passport Center processed my renewal in 3 days. I shipped from the Brewer UPS Store and the whole process was seamless.",
      service: "Emergency Passport",
    },
    {
      name: "Richard T.",
      location: "Holden, ME",
      rating: 5,
      text: "Needed to renew my passport for a business trip to Japan. Fast Passport Center had it done in 5 days. Excellent service for Penobscot County professionals.",
      service: "Passport Renewal",
    },
    {
      name: "Janet S.",
      location: "Brewer, ME",
      rating: 5,
      text: "My son lost his passport three days before our family trip to Canada. Fast Passport Center handled the DS-64 and got him a replacement in 2 days. We crossed the border on schedule.",
      service: "Lost Passport",
    },
    {
      name: "Greg P.",
      location: "Eddington, ME",
      rating: 5,
      text: "I travel internationally for work several times a year. Fast Passport Center has been handling my renewals for years. Consistent, professional, and fast — every single time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "brewer-1",
      name: "Brewer Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "21 Pendleton St",
      city: "Brewer",
      state: "ME",
      zip: "04412",
      phone: "(207) 989-4431",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Brewer",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "brewer-2",
      name: "Bangor Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "22 Second St",
      city: "Bangor",
      state: "ME",
      zip: "04401",
      phone: "(207) 990-0151",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3 miles from downtown Brewer",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Larger facility just across the river. More appointment slots available.",
      icon: "ri-mail-line",
    },
    {
      id: "brewer-3",
      name: "Orono Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1 Bennoch Rd",
      city: "Orono",
      state: "ME",
      zip: "04469",
      phone: "(207) 866-2281",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "10 miles from Brewer",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for University of Maine students. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "brewer-4",
      name: "Holden Town Office",
      type: "Town Office Passport Acceptance",
      address: "207 Main Rd",
      city: "Holden",
      state: "ME",
      zip: "04429",
      phone: "(207) 843-5534",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "8 miles from Brewer",
      website: "https://www.holden.govoffice.com/",
      websiteLabel: "Holden Town Office",
      notes: "Good option for Holden and Clifton residents. Call ahead to schedule.",
      icon: "ri-government-line",
    },
    {
      id: "brewer-5",
      name: "Hampden Town Office",
      type: "Town Office Passport Acceptance",
      address: "106 Western Ave",
      city: "Hampden",
      state: "ME",
      zip: "04444",
      phone: "(207) 862-3034",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "10 miles from Brewer",
      website: "https://www.hampdenmaine.gov/",
      websiteLabel: "Hampden Town Office",
      notes: "Popular for Hampden and Carmel residents. Appointments preferred.",
      icon: "ri-government-line",
    },
    {
      id: "brewer-6",
      name: "Old Town Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "141 Main St",
      city: "Old Town",
      state: "ME",
      zip: "04468",
      phone: "(207) 827-3971",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "12 miles from Brewer",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Old Town and Milford residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Brewer acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Brewer applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Brewer families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Brewer travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Brewer." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Brewer address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Brewer, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Brewer in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Brewer travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Brewer, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Brewer residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Brewer, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Brewer with no office visit required. We serve the greater Bangor area and all of Penobscot County. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Brewer, Maine?",
      a: "The fastest way to get an urgent passport in Brewer is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing or even government expedited mail.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Brewer?",
      a: "Popular options include the Brewer Post Office (04412), Bangor Main Post Office (04401), Orono Post Office (04469), and Holden Town Office (04429). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Bangor, Orono, or Holden?",
      a: "We serve the entire greater Bangor area including Brewer, Bangor, Orono, Holden, Hampden, Old Town, and Milford. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Brewer?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Brewer?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Brewer and Bangor offer passport photo services. We review your photos before submission.",
    },
    {
      q: "What documents do I need for a first-time passport in Brewer?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Brewer?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Brewer families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "How far is Brewer from the Boston Passport Agency?",
      a: "Brewer is approximately 135 miles from the Boston Passport Agency — about a 2.5-hour drive. With Fast Passport Center, you avoid that trip entirely. We courier your documents directly to State Department facilities.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Brewer", address: "307 Wilson St, Brewer, ME 04412", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Brewer", address: "21 Pendleton St, Brewer, ME 04412", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Brewer", address: "21 Pendleton St, Brewer, ME 04412", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Bangor", address: "900 Stillwater Ave, Bangor, ME 04401", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Bangor", address: "650 Main St, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Bangor", address: "499 Main St, Bangor, ME 04401", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Bangor", address: "55 Broadway, Bangor, ME 04401", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Orono", address: "1 Bennoch Rd, Orono, ME 04469", hours: "Mon–Sun: 7AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Brewer",
      address: "21 Pendleton St, Brewer, ME 04412",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Bangor",
      address: "22 Second St, Bangor, ME 04401",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
    {
      name: "BMV — Orono",
      address: "1 Bennoch Rd, Orono, ME 04469",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Convenient for University of Maine students.",
      featured: false,
    },
    {
      name: "BMV — Hampden",
      address: "106 Western Ave, Hampden, ME 04444",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Basic services. Book appointment online.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "3 miles from Brewer (10 minutes)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport — just across the river. The primary departure point for Brewer and the greater Bangor area with domestic flights and seasonal international connections.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "105 miles from Brewer (2 hours)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport. Alternative for more flight options and direct international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "135 miles from Brewer (2.5 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub. Many central Maine travelers connect through Boston for international flights.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
  ],
};